import { FuelButton, FuelDivider, FuelLink, FuelTypography } from '@frete.com/fuel-react';
import { FC } from 'react';

import { ControlledRadioCard, ControlledTextField, Form, Header, TermsModal } from '@/components';
import { TermsFormValues, useTermsFormRules } from '@/hooks';

import * as Styled from './styled';
import { TermsFormProps } from './types';

export const TermsForm: FC<TermsFormProps> = () => {
  const {
    handleSubmit,
    handleModalClose,
    handleSelectNotResponsible,
    handleClickModalCheck,
    handleClickModalReleaseAccess,
    handleClickTermsLink,
    handleClickBack,
    handleClickTermsAgreement,
    validationSchema,
    initialValues,
    loadingResponsibleSending,
    loadingTermsAcceptance,
    modalTermsAgreement,
    isModalOpen,
  } = useTermsFormRules();

  return (
    <>
      <Header title="Libere o acesso para utilizar a GR Fretebras" action={handleClickBack} />
      <Styled.Description>
        Primeiro, precisamos que informe se você é a pessoa responsável pelo Gerenciamento de Risco na sua empresa
      </Styled.Description>
      <Form<TermsFormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({ values, isValid }) => (
          <>
            <Styled.RadioGroup>
              <ControlledRadioCard
                name="responsible"
                value="yes"
                checked={values.responsible === 'yes'}
                title="Sim, sou a pessoa responsável"
                subtitle="Eu cuido dos contratos e aquisições de ferramentas de gerenciamento de risco"
              >
                {values.responsible === 'yes' && (
                  <>
                    <FuelDivider size="large" />
                    <Styled.Section>
                      <FuelTypography color="neutral-strong" size="body-md" weight="bold">
                        Aceite os termos para continuar
                      </FuelTypography>
                      <Styled.Checkbox
                        name="termsAgreement"
                        onClick={handleClickTermsAgreement}
                        label={
                          <FuelTypography>
                            Li e estou de acordo com os{' '}
                            <FuelLink onClick={handleClickTermsLink}>termos de utilização da GR Fretebras</FuelLink>
                          </FuelTypography>
                        }
                      />
                      <FuelTypography color="neutral-default" size="caption" weight="regular">
                        Ao Liberar acesso, você atesta que sua apólice de seguros contempla a Skymark como gerenciadora
                        de risco e não possui nenhuma restrição a essa GR.
                      </FuelTypography>
                    </Styled.Section>
                  </>
                )}
              </ControlledRadioCard>
              <ControlledRadioCard
                name="responsible"
                value="no"
                checked={values.responsible === 'no'}
                title="Não sou a pessoa responsável"
                subtitle="Há outra pessoa responsável por isto na minha empresa"
                onClick={handleSelectNotResponsible}
              >
                {values.responsible === 'no' && (
                  <>
                    <FuelDivider size="large" />
                    <Styled.Section>
                      <FuelTypography color="neutral-strong" size="body-md" weight="bold">
                        Informe os dados da pessoa responsável
                      </FuelTypography>
                      <FuelTypography color="neutral-default" size="body-sm" weight="regular">
                        Entraremos em contato com ela para que sua empresa possa aderir a GR Fretebras
                      </FuelTypography>
                      <Styled.Fields>
                        <ControlledTextField
                          label="Nome do(a) responsável"
                          placeholder="Digite o nome do(a) responsável"
                          name="name"
                        />
                        <ControlledTextField
                          label="Número WhatsApp do(a) responsável"
                          placeholder="Digite o Whatsapp do(a) responsável"
                          mask={['(99) 9999-9999', '(99) 9 9999-9999']}
                          name="phone"
                        />
                      </Styled.Fields>
                      <Styled.Footer>
                        <FuelButton
                          type="submit"
                          size="large"
                          variant="secondary"
                          isLoading={loadingResponsibleSending}
                        >
                          Enviar dados
                        </FuelButton>
                      </Styled.Footer>
                    </Styled.Section>
                  </>
                )}
              </ControlledRadioCard>
            </Styled.RadioGroup>
            <Styled.Footer>
              <FuelButton
                type="submit"
                size="large"
                disabled={!isValid || values.responsible === 'no'}
                isLoading={loadingTermsAcceptance}
              >
                Liberar acesso
              </FuelButton>
            </Styled.Footer>
          </>
        )}
      </Form>
      <TermsModal
        isOpen={isModalOpen}
        isChecked={modalTermsAgreement}
        isButtonLoading={loadingTermsAcceptance}
        onClose={handleModalClose}
        onClickCheck={handleClickModalCheck}
        onClickReleaseAccess={handleClickModalReleaseAccess}
      />
    </>
  );
};
